import React, { useState } from 'react';

import { Card, Col, Image, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';

import { anchovy, backgroundGrey, grape, mediumGray, mutedGray, shadowDark } from 'lib/css/colors';
import CategoryFilter from 'components/shared/CategoryFilter';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { L4_CATEGORY_DIVISION_AVAILABLE_CATEGORIES } from 'lib/networking/endpoints';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import useGet from 'lib/hooks/useGet';
import useTaxonomyOptions from 'lib/hooks/useTaxonomyOptions';

function L4SegmentationSelector({
  onSelectCategory,
  endpoint = L4_CATEGORY_DIVISION_AVAILABLE_CATEGORIES,
}) {
  const [taxonomySearch, setTaxonomySearch] = useState({
    l3Search: '',
    l4Search: '',
  });

  const { options: { l3Options, l4Options } = {} } = useTaxonomyOptions(
    undefined,
    undefined,
    undefined,
    taxonomySearch.l3Search,
    taxonomySearch.l4Search,
    true,
    false,
    false,
  );

  const { data: { data: availableCategories = [] } = {}, loading: loadingAvailable } =
    useGet(endpoint);

  const handleSelectFromSearch = l4Option => {
    if (!l4Option) return;

    const availableMatch = availableCategories.find(c => c.id === l4Option.id);
    if (availableMatch) {
      onSelectCategory(availableMatch);
    } else {
      onSelectCategory({
        id: l4Option.id,
        category_name: l4Option.label,
        product_count: 0,
        default_image_url: null,
      });
    }
  };

  return (
    <div className="px-4">
      <Row className="mb-4">
        <Col md={3}>
          <div className="font-weight-bold mb-2">L3 Category (Optional)</div>
          <CategoryFilter
            categoryOptions={l3Options}
            selectedCategoryName={taxonomySearch.l3Search}
            onChange={option => setTaxonomySearch({ ...taxonomySearch, l3Search: option?.label })}
            placeholder="Filter by L3 Category"
          />
        </Col>
        <Col md={3}>
          <div className="font-weight-bold mb-2">Select L4 Category</div>
          <CategoryFilter
            categoryOptions={l4Options}
            selectedCategoryName={taxonomySearch.l4Search}
            onChange={option => {
              setTaxonomySearch({ ...taxonomySearch, l4Search: option?.label });
              handleSelectFromSearch(option);
            }}
            placeholder="Search for L4 Category"
          />
        </Col>
      </Row>

      <hr className="my-4" />

      <h5 className="font-weight-bold mb-3">Available Categories with Segmentation Data</h5>

      {loadingAvailable ? (
        <LoadingSpinner />
      ) : availableCategories.length === 0 ? (
        <div className={css(styles.emptyMessage)}>No L4 categories found.</div>
      ) : (
        <Row>
          {availableCategories.map(category => (
            <Col key={category.id} md={3} className="mb-4">
              <Card
                className={classNames(css(styles.categoryCard), pointerOnHover)}
                onClick={() => onSelectCategory(category)}
              >
                <Card.Body className="text-center">
                  <Image
                    src={category.default_image_url || placeholderProductImage}
                    className={css(styles.categoryImage)}
                  />
                  <div className={css(styles.categoryName)}>{category.category_name}</div>
                  <div className={css(styles.productCount)}>
                    {category.product_count.toLocaleString()} products
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  categoryCard: {
    border: `2px solid ${anchovy}`,
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: grape,
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${shadowDark}`,
    },
  },
  categoryImage: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    marginBottom: '12px',
    borderRadius: '4px',
    backgroundColor: backgroundGrey,
    padding: '8px',
  },
  categoryName: {
    fontSize: '14px',
    fontWeight: 600,
    color: anchovy,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productCount: {
    fontSize: '12px',
    color: mutedGray,
  },
  emptyMessage: {
    textAlign: 'center',
    padding: '40px',
    color: mediumGray,
    backgroundColor: backgroundGrey,
    borderRadius: '8px',
  },
});

L4SegmentationSelector.propTypes = {
  onSelectCategory: PropTypes.func.isRequired,
  endpoint: PropTypes.string,
};

export default L4SegmentationSelector;
